import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  TrendingDown,
  DollarSign,
  GraduationCap,
  Building,
  Globe,
  Calendar as CalendarIcon,
  Download,
  RefreshCw,
  Filter,
  BarChart3,
  FileText,
  UserCheck,
  Award,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { reportsAPI, adminAPI } from '../../services/api';

// Chart color palette
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

// Status colors mapping
const STATUS_COLORS = {
  PENDING_ADMIN_ASSIGNMENT: '#F59E0B',
  ASSIGNED: '#3B82F6',
  IN_PROGRESS: '#8B5CF6',
  COMPLETED: '#10B981',
  ON_HOLD: '#6B7280',
  CANCELLED: '#EF4444'
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, trend, trendLabel, color = 'blue', loading = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
  >
    <Card className="p-6 bg-white border-stone-200/80 hover:shadow-lg transition-all duration-300 border-l-4" style={{ borderLeftColor: `var(--${color}-500, #3B82F6)` }}>
      {loading ? (
        <div className="animate-pulse">
          <div className="h-12 w-12 bg-stone-200 rounded-xl mb-4" />
          <div className="h-8 w-24 bg-stone-200 rounded mb-2" />
          <div className="h-4 w-32 bg-stone-200 rounded" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center`}>
              <Icon className={`w-6 h-6 text-${color}-600`} />
            </div>
            {trend !== undefined && trend !== null && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                trend >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(trend)}%
              </div>
            )}
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
          <p className="text-sm text-gray-600">{title}</p>
          {trendLabel && <p className="text-xs text-gray-400 mt-1">{trendLabel}</p>}
        </>
      )}
    </Card>
  </motion.div>
);

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Format number with commas
const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value || 0);
};

export default function Reports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    agentId: 'all',
    year: new Date().getFullYear()
  });
  const [datePickerOpen, setDatePickerOpen] = useState({ start: false, end: false });

  // Data states
  const [overview, setOverview] = useState(null);
  const [countryData, setCountryData] = useState([]);
  const [universityData, setUniversityData] = useState([]);
  const [commissionData, setCommissionData] = useState(null);
  const [agentPerformance, setAgentPerformance] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState(null);
  const [yearlyTrends, setYearlyTrends] = useState([]);
  const [agents, setAgents] = useState([]);
  const [countries, setCountries] = useState([]);

  // Build filter params
  const buildParams = useCallback(() => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate.toISOString();
    if (filters.endDate) params.endDate = filters.endDate.toISOString();
    if (filters.agentId !== 'all') params.agentId = filters.agentId;
    return params;
  }, [filters]);

  // Fetch all data
  const fetchAllData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const params = buildParams();

      const results = await Promise.allSettled([
        reportsAPI.getOverview(params),
        reportsAPI.getApplicationsByCountry(params),
        reportsAPI.getApplicationsByUniversity(params),
        reportsAPI.getCommissions(params),
        reportsAPI.getAgentPerformance(params),
        reportsAPI.getMonthlyTrends({ year: filters.year }),
        reportsAPI.getYearlyTrends(),
        adminAPI.getAgents({ limit: 100 }),
        reportsAPI.getCountries()
      ]);

      // Process results
      if (results[0].status === 'fulfilled') setOverview(results[0].value.data);
      if (results[1].status === 'fulfilled') setCountryData(results[1].value.data || []);
      if (results[2].status === 'fulfilled') setUniversityData(results[2].value.data || []);
      if (results[3].status === 'fulfilled') setCommissionData(results[3].value.data);
      if (results[4].status === 'fulfilled') setAgentPerformance(results[4].value.data);
      if (results[5].status === 'fulfilled') setMonthlyTrends(results[5].value.data);
      if (results[6].status === 'fulfilled') setYearlyTrends(results[6].value.data || []);
      if (results[7].status === 'fulfilled') setAgents(results[7].value.agents || []);
      if (results[8].status === 'fulfilled') setCountries(results[8].value.data || []);

      if (showToast) toast.success('Reports refreshed successfully');
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to fetch reports data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildParams, filters.year]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    if (!loading) {
      fetchAllData();
    }
  }, [filters.startDate, filters.endDate, filters.agentId, filters.year]);

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      agentId: 'all',
      year: new Date().getFullYear()
    });
  };

  // Export handler (placeholder)
  const handleExport = async () => {
    toast.info('Export functionality coming soon');
  };

  // Calculate total applications
  const totalApplications = overview?.applications?.reduce((sum, item) => sum + item.count, 0) || 0;

  // Format applications data for pie chart
  const applicationsPieData = overview?.applications?.map(item => ({
    name: item._id?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
    value: item.count,
    color: STATUS_COLORS[item._id] || '#6B7280'
  })) || [];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading reports...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-primary" />
              Reports & Analytics
            </h1>
            <p className="text-gray-600 mt-1">Comprehensive insights into platform performance</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fetchAllData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Quick Access — Report Modules */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <button
              onClick={() => navigate('/admin/reports/campaign-leads')}
              className="w-full text-left group"
            >
              <Card className="p-5 border border-blue-100 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-200 cursor-pointer bg-gradient-to-br from-white to-blue-50/40">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-blue-200 flex-shrink-0">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                        Campaign Leads
                      </h3>
                      <p className="text-xs text-gray-400">Facebook campaigns</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all mt-1" />
                </div>
                <p className="text-sm text-gray-500 leading-relaxed">
                  View and export leads collected from Facebook campaign landing pages.
                </p>
              </Card>
            </button>
          </motion.div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>

            {/* Start Date */}
            <Popover open={datePickerOpen.start} onOpenChange={(open) => setDatePickerOpen(d => ({ ...d, start: open }))}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[140px]">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {filters.startDate ? format(filters.startDate, 'MMM dd, yyyy') : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.startDate}
                  onSelect={(date) => {
                    setFilters(f => ({ ...f, startDate: date }));
                    setDatePickerOpen(d => ({ ...d, start: false }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* End Date */}
            <Popover open={datePickerOpen.end} onOpenChange={(open) => setDatePickerOpen(d => ({ ...d, end: open }))}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[140px]">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {filters.endDate ? format(filters.endDate, 'MMM dd, yyyy') : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.endDate}
                  onSelect={(date) => {
                    setFilters(f => ({ ...f, endDate: date }));
                    setDatePickerOpen(d => ({ ...d, end: false }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Agent Filter */}
            <Select
              value={filters.agentId}
              onValueChange={(value) => setFilters(f => ({ ...f, agentId: value }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {agents.map(agent => (
                  <SelectItem key={agent.userId} value={agent.userId}>
                    {agent.firstName} {agent.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Year Filter */}
            <Select
              value={String(filters.year)}
              onValueChange={(value) => setFilters(f => ({ ...f, year: parseInt(value) }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(year => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear
            </Button>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" className="gap-2">
              <BarChart3 className="w-4 h-4 hidden sm:inline" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="applications" className="gap-2">
              <FileText className="w-4 h-4 hidden sm:inline" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="revenue" className="gap-2">
              <DollarSign className="w-4 h-4 hidden sm:inline" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="agents" className="gap-2">
              <UserCheck className="w-4 h-4 hidden sm:inline" />
              Agents
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp className="w-4 h-4 hidden sm:inline" />
              Trends
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <KPICard
                title="Total Students"
                value={formatNumber(overview?.totals?.students)}
                icon={GraduationCap}
                trend={overview?.trend?.percentage}
                trendLabel="vs last 30 days"
                color="blue"
              />
              <KPICard
                title="Total Agents"
                value={formatNumber(overview?.totals?.agents)}
                icon={Users}
                color="green"
              />
              <KPICard
                title="Universities"
                value={formatNumber(overview?.totals?.universities)}
                icon={Building}
                color="amber"
              />
              <KPICard
                title="Total Applications"
                value={formatNumber(totalApplications)}
                icon={FileText}
                color="purple"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Applications by Status */}
              <Card className="p-6">
                <CardHeader className="px-0 pt-0 pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Applications by Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  {applicationsPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={applicationsPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {applicationsPieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [formatNumber(value), name]}
                        />
                        <Legend
                          layout="vertical"
                          align="right"
                          verticalAlign="middle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      No application data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Countries */}
              <Card className="p-6">
                <CardHeader className="px-0 pt-0 pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    Top Countries by Applications
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  {countryData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={countryData.slice(0, 8)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" />
                        <YAxis dataKey="country" type="category" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => [formatNumber(value), 'Applications']} />
                        <Bar dataKey="totalApplications" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      No country data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Commission Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <p className="text-sm text-green-700 font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-green-800">
                  {formatCurrency(commissionData?.summary?.totalRevenue)}
                </p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <p className="text-sm text-amber-700 font-medium">Pending Payouts</p>
                <p className="text-2xl font-bold text-amber-800">
                  {formatCurrency(commissionData?.summary?.pendingAmount)}
                </p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <p className="text-sm text-blue-700 font-medium">Approved</p>
                <p className="text-2xl font-bold text-blue-800">
                  {formatCurrency(commissionData?.summary?.approvedAmount)}
                </p>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <p className="text-sm text-purple-700 font-medium">Paid Out</p>
                <p className="text-2xl font-bold text-purple-800">
                  {formatCurrency(commissionData?.summary?.paidAmount)}
                </p>
              </Card>
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Country Distribution List */}
              <Card className="p-6">
                <CardHeader className="px-0 pt-0 pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    Country Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {countryData.length > 0 ? countryData.map((item, index) => (
                      <div key={item.country} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-gray-400 w-6">{index + 1}</span>
                          <span className="text-sm font-medium text-gray-900">{item.country}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex gap-2">
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              {item.completed} completed
                            </Badge>
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              {item.inProgress} active
                            </Badge>
                          </div>
                          <span className="text-lg font-bold text-gray-900 min-w-[40px] text-right">
                            {item.totalApplications}
                          </span>
                        </div>
                      </div>
                    )) : (
                      <div className="text-center py-8 text-gray-500">No country data available</div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* University Distribution */}
              <Card className="p-6">
                <CardHeader className="px-0 pt-0 pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building className="w-5 h-5 text-primary" />
                    Top Universities
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pb-0">
                  {universityData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={universityData.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                        <XAxis type="number" />
                        <YAxis
                          dataKey="universityName"
                          type="category"
                          width={150}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => value?.length > 20 ? value.substring(0, 20) + '...' : value}
                        />
                        <Tooltip formatter={(value) => [formatNumber(value), 'Applications']} />
                        <Bar dataKey="totalApplications" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[400px] flex items-center justify-center text-gray-500">
                      No university data available
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Application Status Breakdown */}
            <Card className="p-6">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-lg">Application Status Breakdown by Country</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-stone-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Country</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Total</th>
                        <th className="text-right py-3 px-4 font-medium text-amber-600">Pending</th>
                        <th className="text-right py-3 px-4 font-medium text-blue-600">Assigned</th>
                        <th className="text-right py-3 px-4 font-medium text-purple-600">In Progress</th>
                        <th className="text-right py-3 px-4 font-medium text-green-600">Completed</th>
                        <th className="text-right py-3 px-4 font-medium text-red-600">Cancelled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {countryData.slice(0, 15).map((item) => (
                        <tr key={item.country} className="border-b hover:bg-stone-50/80">
                          <td className="py-3 px-4 font-medium">{item.country}</td>
                          <td className="text-right py-3 px-4 font-bold">{item.totalApplications}</td>
                          <td className="text-right py-3 px-4 text-amber-600">{item.pending}</td>
                          <td className="text-right py-3 px-4 text-blue-600">{item.assigned}</td>
                          <td className="text-right py-3 px-4 text-purple-600">{item.inProgress}</td>
                          <td className="text-right py-3 px-4 text-green-600">{item.completed}</td>
                          <td className="text-right py-3 px-4 text-red-600">{item.cancelled}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            {/* Revenue Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                title="Total Revenue"
                value={formatCurrency(commissionData?.summary?.totalRevenue)}
                icon={DollarSign}
                color="green"
              />
              <KPICard
                title="Pending Payouts"
                value={formatCurrency(commissionData?.summary?.pendingAmount)}
                icon={TrendingUp}
                color="amber"
              />
              <KPICard
                title="Avg Commission"
                value={formatCurrency(commissionData?.summary?.avgCommission)}
                icon={Award}
                color="blue"
              />
              <KPICard
                title="Total Commissions"
                value={formatNumber(commissionData?.summary?.totalCommissions)}
                icon={FileText}
                color="purple"
              />
            </div>

            {/* Monthly Revenue Trend */}
            <Card className="p-6">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Monthly Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                {commissionData?.monthlyTrend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={commissionData.monthlyTrend}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'Revenue']}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke="#10B981"
                        strokeWidth={2}
                        fill="url(#colorRevenue)"
                        name="Revenue"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[350px] flex items-center justify-center text-gray-500">
                    No revenue trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission Status Breakdown */}
            <Card className="p-6">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-lg">Commission Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {commissionData?.byStatus?.map((item) => (
                    <div key={item._id} className="p-4 rounded-lg bg-stone-50 border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-600 capitalize">{item._id}</span>
                        <Badge variant={
                          item._id === 'paid' ? 'default' :
                          item._id === 'approved' ? 'secondary' : 'outline'
                        }>
                          {item.count} items
                        </Badge>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(item.total)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agents Tab */}
          <TabsContent value="agents" className="space-y-6">
            {/* Agent Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                title="Total Agents"
                value={formatNumber(agentPerformance?.summary?.totalAgents)}
                icon={Users}
                color="blue"
              />
              <KPICard
                title="Students Referred"
                value={formatNumber(agentPerformance?.summary?.totalStudentsReferred)}
                icon={GraduationCap}
                color="green"
              />
              <KPICard
                title="Total Earnings"
                value={formatCurrency(agentPerformance?.summary?.totalEarnings)}
                icon={DollarSign}
                color="purple"
              />
              <KPICard
                title="Avg Conversion Rate"
                value={`${agentPerformance?.summary?.avgConversionRate || 0}%`}
                icon={TrendingUp}
                color="amber"
              />
            </div>

            {/* Agent Performance Leaderboard */}
            <Card className="p-6">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  Agent Performance Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-stone-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Rank</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Agent</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Referred</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Applications</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Completed</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Conversion</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agentPerformance?.agents?.length > 0 ? agentPerformance.agents.map((agent, index) => (
                        <tr key={agent.agentId} className="border-b hover:bg-stone-50/80 transition-colors">
                          <td className="py-3 px-4">
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-stone-100 text-gray-700' :
                              index === 2 ? 'bg-amber-100 text-amber-700' :
                              'bg-stone-50 text-gray-500'
                            }`}>
                              {index + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{agent.name}</p>
                              <p className="text-xs text-gray-500">{agent.email}</p>
                            </div>
                          </td>
                          <td className="text-right py-3 px-4 font-medium">{agent.studentsReferred}</td>
                          <td className="text-right py-3 px-4">{agent.totalApplications}</td>
                          <td className="text-right py-3 px-4 text-green-600">{agent.completedApplications}</td>
                          <td className="text-right py-3 px-4">
                            <Badge className={`${
                              agent.conversionRate >= 50 ? 'bg-green-100 text-green-700' :
                              agent.conversionRate >= 25 ? 'bg-amber-100 text-amber-700' :
                              'bg-stone-100 text-gray-700'
                            }`}>
                              {agent.conversionRate}%
                            </Badge>
                          </td>
                          <td className="text-right py-3 px-4 font-semibold text-green-600">
                            {formatCurrency(agent.totalEarnings)}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-gray-500">
                            No agent performance data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            {/* Monthly Trends Chart */}
            <Card className="p-6">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Monthly Trends ({filters.year})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                {monthlyTrends?.chartData?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={monthlyTrends.chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === 'Revenue') return [formatCurrency(value), name];
                          return [formatNumber(value), name];
                        }}
                      />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="students"
                        stroke="#3B82F6"
                        strokeWidth={2}
                        dot={{ fill: '#3B82F6', r: 4 }}
                        name="New Students"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="applications"
                        stroke="#10B981"
                        strokeWidth={2}
                        dot={{ fill: '#10B981', r: 4 }}
                        name="Applications"
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="completedApplications"
                        stroke="#8B5CF6"
                        strokeWidth={2}
                        dot={{ fill: '#8B5CF6', r: 4 }}
                        name="Completed"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        stroke="#F59E0B"
                        strokeWidth={2}
                        dot={{ fill: '#F59E0B', r: 4 }}
                        name="Revenue"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[400px] flex items-center justify-center text-gray-500">
                    No monthly trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Yearly Comparison */}
            <Card className="p-6">
              <CardHeader className="px-0 pt-0 pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Year-over-Year Comparison
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                {yearlyTrends.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={yearlyTrends}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="year" />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === 'Revenue') return [formatCurrency(value), name];
                            return [formatNumber(value), name];
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="students" fill="#3B82F6" name="Students" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="left" dataKey="applications" fill="#10B981" name="Applications" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="right" dataKey="revenue" fill="#F59E0B" name="Revenue" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>

                    {/* Yearly Stats Table */}
                    <div className="mt-6 overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-stone-50">
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Year</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Students</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Applications</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Completed</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Revenue</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Growth</th>
                          </tr>
                        </thead>
                        <tbody>
                          {yearlyTrends.map((item) => (
                            <tr key={item.year} className="border-b hover:bg-stone-50/80">
                              <td className="py-3 px-4 font-medium">{item.year}</td>
                              <td className="text-right py-3 px-4">{formatNumber(item.students)}</td>
                              <td className="text-right py-3 px-4">{formatNumber(item.applications)}</td>
                              <td className="text-right py-3 px-4 text-green-600">{formatNumber(item.completedApplications)}</td>
                              <td className="text-right py-3 px-4 font-semibold">{formatCurrency(item.revenue)}</td>
                              <td className="text-right py-3 px-4">
                                {item.growth && (
                                  <div className="flex items-center justify-end gap-1">
                                    {item.growth.revenue >= 0 ? (
                                      <ArrowUpRight className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <ArrowDownRight className="w-4 h-4 text-red-600" />
                                    )}
                                    <span className={item.growth.revenue >= 0 ? 'text-green-600' : 'text-red-600'}>
                                      {Math.abs(item.growth.revenue)}%
                                    </span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-gray-500">
                    No yearly trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monthly Totals Summary */}
            {monthlyTrends?.totals && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Total Students ({filters.year})</p>
                  <p className="text-2xl font-bold text-blue-600">{formatNumber(monthlyTrends.totals.students)}</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Total Applications ({filters.year})</p>
                  <p className="text-2xl font-bold text-green-600">{formatNumber(monthlyTrends.totals.applications)}</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Completed ({filters.year})</p>
                  <p className="text-2xl font-bold text-purple-600">{formatNumber(monthlyTrends.totals.completedApplications)}</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Revenue ({filters.year})</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(monthlyTrends.totals.revenue)}</p>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
